import { Component, OnDestroy, OnInit, ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";

import { UserService } from "../../shared/services/user.service";
import { DialogsService } from "../../shared/services/dialogs/dialogs.service";
import { UserBookingService } from "../../shared/services/user-booking.service";
import { Booking } from "../../shared/models/booking.model";
import { Client } from "../../shared/models/client.model";
import { Service } from "../../shared/models/service.model";
import { BookingService } from "../../shared/services/booking.service";
import { ClientService } from "../../shared/services/client.service";
import { SalonService } from "../../shared/services/salon.service";
import {
  NotificationService,
  NOTIFICATION_TYPE,
} from "../../shared/services/notification.service";
import { Utilities } from "../../shared/utilities/utilities";
// tslint:disable-next-line:import-blacklist
import { EMPTY, from, Observable, of, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { Stylist } from "../../shared/models/stylist.model";
import { StripeService } from "../../shared/services/stripe.service";
import { StylistService } from "../../shared/services/stylist.service";
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from "../../shared/services/language.service";

@Component({
  templateUrl: "bookings-summary.component.html",
})
export class BookingsSummaryComponent implements OnInit, OnDestroy {
  public booking: Booking;

  public title: string;

  public subTitle: string;
  public chargeOnNoShow = true;
  public loadStripeComponent = false;
  public clientHasStripeAccount = false;
  public isLoading = false;
  public refreshStripe = 0;
  //todo: pull chargeNoShow from salon service (true if  clientBookingPayments > 0)
  public clientBookingPayments: number;
  public noShowPercentage: number;
  public isAnyBooking: boolean;
  public enableBookButton: boolean;
  public savingStripe: boolean = false;
  private languageSubscription: Subscription;
  constructor(
    public userService: UserService,
    private bookingService: BookingService,
    private userBookingService: UserBookingService,
    private salonService: SalonService,
    private router: Router,
    private dialogsService: DialogsService,
    private clientService: ClientService,
    private stripeService: StripeService,
    private stylistService: StylistService,
    private notificationService: NotificationService,
    private snackBar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private languageService: LanguageService
  ) {
    this.booking = this.userBookingService.getBooking();
    this.refreshLocalizedText(this.languageService.getCurrentLanguage());
    this.languageSubscription = this.languageService.currentLanguage$.subscribe((lang: string) => {
      this.refreshLocalizedText(lang);
    });
    this.isAnyBooking = this.booking
      .getServices()
      .some(
        (service) =>
          service.getServiceDefinition().getCommonStylists()?.size > 0
      );
    this.enableBookButton = true;
  }

  private refreshLocalizedText(lang: string): void {
    this.title = this.translateService.instant('BOOKINGS.TITLE_SO_FAR');
    this.subTitle = Utilities.formatDate(
      this.booking.getStartDateTime(),
      "h:mm a, EEEE, MMMM d, yyyy",
      lang
    );
  }

  public async ngOnInit() {
    if (
      (this.booking.getStatus() === 2 || this.booking.getStatus() === 3) &&
      this.booking.getServices().length === 0
    ) {
      await this.router.navigate(["/"]);
    }

    this.clientBookingPayments = this.userService
      .getSalon()
      .getClientBookingPayments();
  }

  public ngOnDestroy(): void {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  public onPhotoUpdate(photos: Array<string>) {
    this.booking.setPhotos(photos);
  }

  public onBookingSave() {
    this.enableBookButton = false;
    if (this.userService.isLoggedIn()) {
      if (this.checkStylistsSelected()) {
        this.save(this.userService.getUser());
      } else {
        this.enableBookButton = true;
      }
    } else {
      let allowSignUp = true;
      let allowFBLogin = true;
      let clientBookingPayments = this.clientBookingPayments;

      // if one of the stylists in this booking has getLetClientsRegister turned off,
      // disable client sign up
      for (let service of this.booking.getServices()) {
        if (service.getServiceDefinition()) {
          const stylist = service.getStylist();

          if (stylist && stylist.getLetClientsRegister() === 0) {
            allowSignUp = false;
            // break;
          }
          if (stylist && stylist.getLetClientsUseFBLogin() === 0) {
            allowFBLogin = false;
            // break;
          }
        }
      }

      this.dialogsService
        .login(allowSignUp, allowFBLogin)
        .subscribe((client: Client) => {
          // console.log('dialog closed - triggering change');

          if (client) {
            if (this.clientBookingPayments > 0) {
              // salon requires payment on booking so we will return to the page and collect payment info:
              // this.checkUserStripeAccount();
              this.refreshStripe++;
              this.enableBookButton = true;
              // console.log("client info after login: ", client);
            } else {
              this.save(client);
            }
          } else {
            this.enableBookButton = true;
          }
        });
    }
  }

  private checkStylistsSelected(): boolean {
    for (let service of this.booking.getServices()) {
      if (service.getServiceDefinition()) {
        const stylist = service.getStylist();
        if (!stylist) {
          this.snackBar.open(this.translateService.instant('BOOKINGS.SELECT_SERVICE_PROVIDER'), this.translateService.instant('DIALOG.CLOSE'), {
            duration: 5000,
            panelClass: ["error-snackbar"],
            horizontalPosition: "center",
            verticalPosition: "top",
          });
          // Reactivate the "Book it" button
          this.savingStripe = false;
          // Trigger change detection
          this.changeDetectorRef.detectChanges();
          return false;
        }
      }
    }
    return true;
  }

  private save(client: Client) {
    this.isLoading = true;
    let notificationType: NOTIFICATION_TYPE = NOTIFICATION_TYPE.NEW_BOOKING;
    this.booking.setClient(client);
    this.clientService.getClientInfo().subscribe(
      (client: Client) => {
        this.userService.setUser(client);
        console.log("client", client);
        console.log("userService.getUser().isBlockedOnline()", this.userService.getUser().isBlockedOnline());
        if (this.userService.getUser().isBlockedOnline()) {
          this.dialogsService
            .alert(
              this.translateService.instant('BOOKINGS.BLOCKED_ONLINE_MESSAGE'),
              this.translateService.instant('BOOKINGS.CONTACT_SALON_PHONE')
            )
            .subscribe(() => {
              this.userBookingService.resetBooking();
              this.router.navigate(["/"]);
            });
          this.enableBookButton = true;
          return;
        }

        // default (Unconfirmed status)
        let status = 3;

        // if one of the stylists in this booking has confirmAppointmentsManually turned on,
        // set the booking status to requested
        for (let service of this.booking.getServices()) {
          if (service.getServiceDefinition()) {
            const stylist = service.getStylist();
            if (stylist && stylist.getConfirmApptsManually() === 1) {
              // requested
              status = 2;
              notificationType = NOTIFICATION_TYPE.NEW_BOOKING_REQUEST;
            }
          }
        }

        if (this.booking.getId()) {
          notificationType = NOTIFICATION_TYPE.BOOKING_EDITED;
        }

        // any-provider flow: the service's lineitem stylist is undefined, but
        // the serviceDefinition has the stylist who will actually be booked.
        // Fetch full stylist data to check their confirmApptsManually setting.
        const needsStylistLookup = status !== 2 && this.isAnyBooking;
        const stylistCheck$: Observable<Stylist[]> = needsStylistLookup
          ? this.stylistService.getStylists(this.userService.getSalon().getId())
          : of([] as Stylist[]);

        stylistCheck$.pipe(
          switchMap((stylists: Stylist[]) => {
            if (needsStylistLookup && stylists.length > 0) {
              for (const service of this.booking.getServices()) {
                if (service.getServiceDefinition()) {
                  const sdStylistID = service.getServiceDefinition().getStylist()?.getID();
                  const fullStylist = stylists.find(s => s.getID() === sdStylistID);
                  if (fullStylist && fullStylist.getConfirmApptsManually() === 1) {
                    status = 2;
                    notificationType = NOTIFICATION_TYPE.NEW_BOOKING_REQUEST;
                    break;
                  }
                }
              }
            }
            return this.checkBookingOverlaps();
          }),
          switchMap((overlaps: Array<Service>) => {
              if (overlaps.length > 0) {
                this.dialogsService
                  .alert(
                    this.translateService.instant('BOOKINGS.OVERLAP_TITLE'),
                    this.translateService.instant('BOOKINGS.OVERLAP_MESSAGE')
                  )
                  .subscribe();
                this.router.navigate(["stylists/availabilities"]);
                return EMPTY;
              }

              this.booking.setStatus(status);
              return from(this.imageUpload(client, this.booking.getPhotos()));
            }),
            switchMap((booking) => {
              return this.bookingService.saveBooking(booking);
            })
          )
          .subscribe(
            (booking: Booking) => {
              if (!booking) {
                this.enableBookButton = true;
                this.isLoading = false;
                return;
              }

              this.notificationService
                .send(booking, notificationType)
                .subscribe(() => {
                  this.isLoading = false;
                  this.router.navigate(["bookings/confirmation"]);
                });
            },
            (error) => {
              this.isLoading = false;
              this.enableBookButton = true;
              throw error;
              // this.dialogsService.errorAlert().subscribe();
            }
          );
      },
      (error) => {
        console.log("error on save:", error);
        if (error.error.err_msg === "Client not found") {
          console.log("err msg client not found");
          this.enableBookButton = true;
          // user not found, log out and instead of saving make them log in first
          this.userService.logout();
          this.onBookingSave();
        } else {
          throw error;
        }
        // this.dialogsService.errorAlert().subscribe();
      }
    );
  }

  private checkBookingOverlaps(): Observable<Array<Service>> {
    return new Observable<Array<Service>>((observer) => {
      let overlaps: Array<Service> = [];
      let counter: number = 0;
      let successCount: number = 0;
      console.log("booking services:", this.booking.getServices());
      for (let service of this.booking.getServices()) {
        console.log("in services loop:", service);
        this.bookingService
          .bookingOverlapChecker(this.booking, service)
          .subscribe(
            (result) => {
              successCount++;
              overlaps.push(...result);

              if (successCount === this.booking.getServices().length) {
                observer.next(overlaps);
                observer.complete();
              }
            },
            (err) => {
              observer.error(err);
              observer.complete();
            }
          );
      }
    });
  }

  imageUpload(client: Client, photos: Array<string>) {
    let photoUploadPromises = photos.map((photo) =>
      this.clientService.insertClientPhoto(client.getID(), photo).toPromise()
    );

    return Promise.all(photoUploadPromises).then((photoRes) => {
      photoRes = photoRes.map((res) => res.data);
      this.booking.setPhotos(photoRes);
      return this.booking;
    });
  }
}
